/**
 * SnapshotDiffViewer - Fase 7: Vista de Diff Visual
 * 
 * Componente para mostrar comparación visual entre un snapshot y el estado actual.
 * 
 * Características:
 * - Tabs para cada tipo de diff (Código, Chat, Config, Estado)
 * - Colores: Verde (agregado), Rojo (eliminado), Amarillo (modificado)
 * - Vista de código con highlighting de cambios
 */

import React, { useState, useEffect } from 'react';
import { X, Code, MessageSquare, Settings, Database, GitCompare, ChevronDown, ChevronRight, Plus, Minus, Edit3, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const SnapshotDiffViewer = ({ snapshotId, onClose }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [activeTab, setActiveTab] = useState('workspace');
  const [expandedItems, setExpandedItems] = useState({});

  const txt = {
    es: {
      title: 'Comparación de Estados',
      subtitle: 'Diferencias entre el snapshot y el estado actual',
      loading: 'Cargando diff...',
      error: 'Error al cargar diff',
      noChanges: 'Sin cambios',
      tabs: {
        workspace: 'Código',
        chat: 'Chat',
        config: 'Configuración',
        state: 'Estado'
      },
      changes: 'cambios',
      added: 'Agregado',
      removed: 'Eliminado',
      modified: 'Modificado',
      unchanged: 'Sin cambios',
      close: 'Cerrar',
      totalChanges: 'Total de cambios',
      snapshotInfo: 'Snapshot'
    },
    en: {
      title: 'State Comparison',
      subtitle: 'Differences between snapshot and current state',
      loading: 'Loading diff...',
      error: 'Error loading diff',
      noChanges: 'No changes',
      tabs: {
        workspace: 'Code',
        chat: 'Chat',
        config: 'Config',
        state: 'State'
      },
      changes: 'changes',
      added: 'Added',
      removed: 'Removed',
      modified: 'Modified',
      unchanged: 'Unchanged',
      close: 'Close',
      totalChanges: 'Total changes',
      snapshotInfo: 'Snapshot'
    }
  };

  const t = txt[language] || txt.es;

  useEffect(() => {
    fetchDiff();
  }, [snapshotId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDiff = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/snapshots/${snapshotId}/diff`
      );
      
      if (!response.ok) {
        throw new Error('Error fetching diff');
      }
      
      const data = await response.json();
      setDiffData(data);
    } catch (err) {
      console.error('Error loading diff:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'added':
        return <Plus className="w-4 h-4 text-green-400" />;
      case 'removed':
        return <Minus className="w-4 h-4 text-red-400" />;
      case 'modified':
        return <Edit3 className="w-4 h-4 text-amber-400" />;
      default:
        return null;
    }
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case 'added':
        return 'bg-green-500/10 border-green-500/30 text-green-400';
      case 'removed':
        return 'bg-red-500/10 border-red-500/30 text-red-400';
      case 'modified':
        return 'bg-amber-500/10 border-amber-500/30 text-amber-400';
      default:
        return 'bg-zinc-800 border-zinc-700 text-zinc-400';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      added: t.added,
      removed: t.removed,
      modified: t.modified,
      unchanged: t.unchanged
    };
    return labels[type] || type;
  };

  const renderDiffItem = (item, index) => {
    const itemId = `${item.path}-${index}`;
    const isExpanded = expandedItems[itemId];
    const hasDetails = item.details && item.details.length > 0;

    return (
      <div
        key={itemId}
        className={`rounded-lg border ${getTypeStyles(item.type)} p-3 mb-2`}
      >
        <div 
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => hasDetails && toggleExpand(itemId)}
        >
          {hasDetails && (
            isExpanded 
              ? <ChevronDown className="w-4 h-4 text-zinc-400" />
              : <ChevronRight className="w-4 h-4 text-zinc-400" />
          )}
          {!hasDetails && <div className="w-4" />}
          
          {getTypeIcon(item.type)}
          
          <div className="flex-1">
            <div className="font-mono text-sm">{item.path}</div>
            {item.before && item.after && (
              <div className="text-xs text-zinc-500 mt-1">
                <span className="text-red-400">{item.before}</span>
                <span className="mx-2">→</span>
                <span className="text-green-400">{item.after}</span>
              </div>
            )}
            {item.before && !item.after && (
              <div className="text-xs text-red-400 mt-1">{item.before}</div>
            )}
            {!item.before && item.after && (
              <div className="text-xs text-green-400 mt-1">{item.after}</div>
            )}
          </div>
          
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeStyles(item.type)}`}>
            {getTypeLabel(item.type)}
          </span>
        </div>

        {isExpanded && hasDetails && (
          <div className="mt-3 pt-3 border-t border-zinc-700">
            <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap bg-zinc-900/50 p-3 rounded">
              {renderCodeDiff(item.details)}
            </pre>
          </div>
        )}
      </div>
    );
  };

  const renderCodeDiff = (diffText) => {
    if (!diffText) return null;
    
    return diffText.split('\n').map((line, i) => {
      let lineClass = 'text-zinc-400';
      if (line.startsWith('+') && !line.startsWith('+++')) {
        lineClass = 'text-green-400 bg-green-500/10';
      } else if (line.startsWith('-') && !line.startsWith('---')) {
        lineClass = 'text-red-400 bg-red-500/10';
      } else if (line.startsWith('@@')) {
        lineClass = 'text-cyan-400';
      }
      
      return (
        <div key={i} className={`${lineClass} px-1`}>
          {line || ' '}
        </div>
      );
    });
  };

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
      <GitCompare className="w-12 h-12 mb-4 opacity-50" />
      <p>{t.noChanges}</p>
    </div>
  );

  const renderDiffList = (items) => {
    if (!items || items.length === 0) {
      return renderEmptyState();
    }
    
    return (
      <div className="space-y-2">
        {items.map((item, index) => renderDiffItem(item, index))}
      </div>
    );
  };

  const getTabContent = () => {
    if (!diffData?.diff) return renderEmptyState();
    
    switch (activeTab) {
      case 'workspace':
        return renderDiffList(diffData.diff.workspace_code_diff);
      case 'chat':
        return renderDiffList(diffData.diff.chat_history_diff);
      case 'config':
        return renderDiffList(diffData.diff.user_config_diff);
      case 'state':
        return renderDiffList(diffData.diff.system_state_diff);
      default:
        return renderEmptyState();
    }
  };

  const getTabCount = (tabKey) => {
    if (!diffData?.diff) return 0;
    
    const mapping = {
      workspace: 'workspace_code_diff',
      chat: 'chat_history_diff',
      config: 'user_config_diff',
      state: 'system_state_diff'
    };
    
    return diffData.diff[mapping[tabKey]]?.length || 0;
  };

  const tabs = [
    { key: 'workspace', icon: Code },
    { key: 'chat', icon: MessageSquare },
    { key: 'config', icon: Settings },
    { key: 'state', icon: Database }
  ];

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-zinc-400">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-zinc-900 border border-red-500/30 rounded-xl p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{t.error}</p>
          <p className="text-zinc-500 text-sm mb-4">{error}</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-300"
          >
            {t.close}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-cyan-400" />
              {t.title}
            </h2>
            <p className="text-sm text-zinc-500 mt-1">{t.subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Snapshot Info */}
        {diffData && (
          <div className="px-4 py-3 bg-zinc-800/50 border-b border-zinc-700 flex items-center justify-between">
            <div className="text-sm">
              <span className="text-zinc-500">{t.snapshotInfo}: </span>
              <span className="text-zinc-300">{diffData.snapshot_summary || snapshotId}</span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-zinc-500">
                {t.totalChanges}: 
                <span className={`ml-1 font-semibold ${diffData.summary?.has_changes ? 'text-amber-400' : 'text-green-400'}`}>
                  {diffData.summary?.total_changes || 0}
                </span>
              </span>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-zinc-700">
          {tabs.map(({ key, icon: Icon }) => {
            const count = getTabCount(key);
            const isActive = activeTab === key;
            
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                  isActive 
                    ? 'bg-zinc-800 text-cyan-400 border-b-2 border-cyan-400' 
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{t.tabs[key]}</span>
                {count > 0 && (
                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                    isActive ? 'bg-cyan-500/20 text-cyan-400' : 'bg-zinc-700 text-zinc-400'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {getTabContent()}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-zinc-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-300 transition-colors"
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SnapshotDiffViewer;
